/**
 * Cobertura por loja (aba Oportunidades, formato aprovado pelo Felipe em 06/10/2026): placar por loja + matriz SKU × loja
 * ordenada pela meta sem canal em R$; a linha abre os anúncios por trás de cada selo. ML, Shopee e Shein pela API dos
 * anúncios; TikTok e Amazon sem leitura de anúncio (só vendas).
 */
import { useState } from 'react'
import {
  Box, Card, CardContent, Chip, Collapse, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import { Seta } from './componentes'
import { brl, brl2, COBERTURA, coberturaPorLoja, dataBr, type LinhaCobertura, nomeLoja, num, pct, placarLojas } from './logic'
import type { NatalData, SkuNatal } from './types'

const CHEGANDO = '__chegando'

function Selo({ x, loja }: { x: LinhaCobertura; loja: string }) {
  const c = x.cel[loja]
  if (c.st === 'ativo') return <Chip size="small" color="success" label={`no ar · ${c.noAr}${c.kits ? ` (${c.kits} kit)` : ''}`} />
  if (c.st === 'chega') return <Chip size="small" variant="outlined" label={x.chegada ? `chega ${dataBr(x.chegada)}` : 'a caminho'} />
  const [t, cor] = COBERTURA[c.st] ?? [c.st || '?', 'default' as const]
  return <Chip size="small" color={cor} variant={['sem_leitura', 'outro'].includes(c.st) ? 'outlined' : 'filled'} label={t} title={c.motivo ?? undefined} />
}

function Linha({ x, d, aberto, onAlternar, onAbrirSku }: { x: LinhaCobertura; d: NatalData; aberto: boolean; onAlternar: () => void; onAbrirSku: (s: string) => void }) {
  return (
    <>
      <TableRow hover onClick={onAlternar} sx={{ cursor: 'pointer', '& > td': { borderBottom: aberto ? 0 : undefined } }}>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <Seta aberta={aberto} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" component="span" color="primary" fontWeight={700} sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                onClick={(e) => { e.stopPropagation(); onAbrirSku(x.k.sku) }}>{x.k.sku}</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 220, whiteSpace: 'normal' }}>{x.k.descricao}</Typography>
            </Box>
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {x.pecas > 0
            ? <>{num(x.pecas)} pç<Typography variant="caption" color="text.secondary" display="block">{brl(x.custoEstoque)}</Typography></>
            : <Typography variant="caption" color="text.secondary">{x.chegada ? `chega ${dataBr(x.chegada)}` : 'sem estoque'}</Typography>}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <Typography variant="body2" color={x.semCanalRs > 0 ? 'error.main' : 'text.secondary'} fontWeight={600}>{brl(x.semCanalRs)}</Typography>
          <Typography variant="caption" color="text.secondary">de {brl(x.metaRs)}</Typography>
        </TableCell>
        {d.lojas.map((l) => <TableCell key={l.store_key} align="center"><Selo x={x} loja={l.store_key} /></TableCell>)}
      </TableRow>
      <TableRow>
        <TableCell colSpan={3 + d.lojas.length} sx={{ py: 0, bgcolor: aberto ? 'action.hover' : undefined, borderBottom: aberto ? undefined : 0 }}>
          <Collapse in={aberto} unmountOnExit>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1, py: 1.5, pl: 3 }}>
              {d.lojas.map((l) => {
                const c = x.cel[l.store_key]
                return (
                  <Box key={l.store_key} sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{nomeLoja(d, l.store_key)}</Typography>
                    {c.anuncios.length
                      ? c.anuncios.map((a) => (
                        <Box key={a.id} sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline', mt: 0.5 }}>
                          <Chip size="small" variant={a.status === 'ativo' || a.status === 'pausado' ? 'filled' : 'outlined'}
                            color={a.status === 'ativo' ? 'success' : a.status === 'pausado' ? 'warning' : 'default'}
                            label={a.status === 'ativo' ? 'no ar' : a.status === 'pausado' ? 'pausado' : a.status === 'vende' ? 'vendendo' : 'outro status'} />
                          <Typography variant="caption" sx={{ minWidth: 0 }}>
                            {a.titulo}{!(a.comps.length === 1 && a.comps[0][1] === 1) && <Typography component="span" variant="caption" color="text.secondary"> (kit)</Typography>}
                            <Typography variant="caption" color="text.secondary" display="block">
                              {a.id}{a.preco ? ` · ${brl2(a.preco)}` : ''}{a.status === 'outro' && a.status_api ? ` · ${a.status_api === 'under_review' ? 'em revisão no ML' : a.status_api === 'fora_do_ar' ? 'fora do ar na Shein' : a.status_api}` : ''}
                            </Typography>
                          </Typography>
                        </Box>
                      ))
                      : <Typography variant="caption" color="text.secondary" display="block">{c.st === 'sem_leitura' ? 'Sem leitura de anúncio nesta loja.' : 'Nenhum anúncio.'}</Typography>}
                  </Box>
                )
              })}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function CoberturaLojas({ d, ks, onAbrirSku }: { d: NatalData; ks: SkuNatal[]; onAbrirSku: (sku: string) => void }) {
  const cob = coberturaPorLoja(d, ks)
  const placar = placarLojas(d, cob)
  const [abertos, setAbertos] = useState<Set<string>>(new Set())
  const alternar = (k: string) => setAbertos((a) => { const n = new Set(a); if (n.has(k)) n.delete(k); else n.add(k); return n })
  const colunas = 3 + d.lojas.length
  return (
    <Card variant="outlined"><CardContent>
      <Typography variant="h6">Cobertura por loja</Typography>
      <Typography variant="caption" color="text.secondary">
        Onde cada SKU está à venda. Maior meta sem canal primeiro; clique na linha para ver os anúncios por trás de cada selo.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1, my: 1.5 }}>
        {placar.map((p) => (
          <Box key={p.loja.store_key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>{pct(p.meta)} da meta</Typography>
            <Typography variant="body2" fontWeight={600}>{p.loja.nome}</Typography>
            <Typography variant="h6">{p.semLeitura ? '—' : `${num(p.noAr)} de ${num(cob.comEstoque.length)}`}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">{p.semLeitura ? 'sem leitura de anúncio' : 'SKUs com estoque no ar'}</Typography>
            <Typography variant="caption" color="error.main" fontWeight={600}>{brl(p.semCanalRs)} sem canal</Typography>
          </Box>
        ))}
      </Box>
      <TableContainer sx={{ maxHeight: '70vh', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>Estoque</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Meta sem canal</TableCell>
            {d.lojas.map((l) => <TableCell key={l.store_key} align="center" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{l.nome} ({l.meta_com ? `meta com ${d.lojas.find((x) => x.store_key === l.meta_com)?.nome ?? l.meta_com}` : pct(d.premissas.meta_participacao_loja[l.store_key] ?? 0)})</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {cob.comEstoque.map((x) => <Linha key={x.k.sku} x={x} d={d} aberto={abertos.has(x.k.sku)} onAlternar={() => alternar(x.k.sku)} onAbrirSku={onAbrirSku} />)}
            {cob.aChegar.length > 0 && (
              <>
                <TableRow hover onClick={() => alternar(CHEGANDO)} sx={{ cursor: 'pointer', bgcolor: 'action.hover' }}>
                  <TableCell colSpan={colunas}>
                    <Stack direction="row" spacing={1} alignItems="center"><Seta aberta={abertos.has(CHEGANDO)} />
                      <Typography variant="body2"><b>Aguardando chegada</b> · {cob.aChegar.length} SKUs · {brl(cob.aChegar.reduce((a, x) => a + x.metaRs, 0))} de meta · preparar o cadastro antes do lote entrar</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
                {abertos.has(CHEGANDO) && cob.aChegar.map((x) => <Linha key={x.k.sku} x={x} d={d} aberto={abertos.has(x.k.sku)} onAlternar={() => alternar(x.k.sku)} onAbrirSku={onAbrirSku} />)}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Selos: <b>no ar</b> (n anúncios; kit conta para o componente) · <b>pausado</b> só status pausado · <b>outro status</b> o anúncio existe mas não
        está no ar nem pausado (motivo ao passar o mouse) · <b>sem anúncio</b> o anúncio não existe · <b>chega DD/MM</b> item de container a caminho ·
        <b> sem leitura</b> loja sem leitura de anúncio. Fontes: ML Matriz, ML Extrema, Shopee Matriz e Shopee Decora pela API dos anúncios; Shein pela API de produtos
        {d.meta.fontes.anuncios_shein?.lido_em ? '' : ' (leitura indisponível agora: Shein pelas vendas de 14 dias)'}; TikTok e Amazon pelas vendas.
        Meta sem canal = meta da temporada do SKU (a custo) × fatia das lojas onde ele não está no ar.
      </Typography>
    </CardContent></Card>
  )
}
